import { FC } from 'react';
import { beautifyThePrice, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { fixNumber } from '@/common/utils/ways';
import { CommercialConditionDetail } from '../CommercialCondition';
import { BrandSynergyModule, CostAnalysis, SalesEstimation, SimilarStoreModule, SmartDecisionSummary, SmartDecisionValue } from './types';
import styles from './index.module.less';
import V2Table from '@/common/components/Data/V2Table';
import { higherRentSettlementText } from '../../rentPresentation';
// 实付租金与含物业费/推广费的月度合计分开展示；财务指标均消费服务端结果。
import IconFont from '@/common/components/IconFont';

interface ConclusionData {
  mallLevel?: string;
  summary?: SmartDecisionSummary;
  similarStoreModule?: SimilarStoreModule;
  salesEstimation?: SalesEstimation;
  brandSynergyModule?: BrandSynergyModule;
  costAnalysis?: CostAnalysis;
  commercialConditionDetail?: CommercialConditionDetail;
}

interface ConclusionProps {
  data?: ConclusionData;
}

/**
 * @description 智能测算与决策结论
 */
const Conclusion: FC<ConclusionProps> = ({ data }) => {
  const summary = data?.summary;
  const methods = useMethods({
    formatNumber(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return beautifyThePrice(value || '', ',', 'auto');
    },
    formatWan(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return String(value);
      // 绝对值达到一万时按“万”为单位展示，保留一位小数，负数同样按万展示（如 -78.9万）
      if (Math.abs(numberValue) >= 10000) return `${fixNumber(numberValue / 10000, 1)}万`;
      return methods.formatNumber(value);
    },
    formatPercent(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return `${value}%`;
    },
    formatMoney(value: SmartDecisionValue, suffix: string) {
      if (!isNotEmpty(value)) return '-';
      return `${methods.formatNumber(value)}${suffix}`;
    },
    formatMallLevel(value?: string) {
      if (!isNotEmpty(value)) return '-';
      return String(value).endsWith('级') ? String(value) : `${value}级`;
    },
    formatCoefficient(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return String(value).startsWith('系数') ? String(value) : `系数 ${value}`;
    },
    getSynergyDescription(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '';
      const coefficient = Number(value);
      if (coefficient >= 1.1) return '邻近品牌协同效应较强';
      if (coefficient >= 1) return '邻近品牌协同效应一般';
      return '邻近品牌存在分流效应';
    },
    getFlowAdvantage() {
      const dailyFlow = Number(data?.salesEstimation?.dailyFlow);
      const averageFlow = Number(data?.similarStoreModule?.average?.passbyFlow);
      if (!Number.isFinite(dailyFlow) || !Number.isFinite(averageFlow) || averageFlow === 0) return '';
      const advantage = dailyFlow - averageFlow;
      const advantagePercent = advantage / averageFlow * 100;
      return `${advantage >= 0 ? '高于' : '低于'}相似店均值 ${Math.abs(advantagePercent).toFixed(1)}%`;
    },
    hasSimilarStoreBaseline() {
      return isNotEmpty(data?.similarStoreModule?.average?.dailySales);
    },
    getBusinessDescription() {
      const businessType = String(data?.commercialConditionDetail?.businessType);
      const deductionRate = isNotEmpty(data?.commercialConditionDetail?.deductionRate)
        ? `${data?.commercialConditionDetail?.deductionRate}%`
        : '-';
      if (businessType === '1') return '租金按保底租金计';
      if (businessType === '2') return `租金按扣点 ${deductionRate} 计`;
      if (businessType === '3') return `租金按保底租金与扣点 ${deductionRate} 计`;
      if (businessType === '4') return higherRentSettlementText(data?.commercialConditionDetail, methods.formatMonthlyRent) || '租金按扣点与保底租金取高';
      return '租金及费用按商务条件计';
    },
    getMonthlyRentAndExpense() {
      return data?.costAnalysis?.monRentAndExpense ?? data?.commercialConditionDetail?.monthlyRentAndFeeTotal;
    },
    formatMonthlyRent(value: number | string) {
      return methods.formatMoney(value, '元/月');
    },
    getOneTimeInvestment() {
      return data?.costAnalysis?.oneTimeInvestment;
    },
    getEvaluationItems() {
      const priceToRentRatio = Number(summary?.priceToRentRatio);
      const paybackPeriod = Number(summary?.paybackPeriod);
      const netProfitRate = Number(summary?.netProfitRate);
      return [
        {
          label: '租售比',
          rule: '<20%',
          value: methods.formatPercent(summary?.priceToRentRatio),
          qualified: Number.isFinite(priceToRentRatio) && priceToRentRatio > 0 && priceToRentRatio < 20,
        },
        {
          label: '回本周期',
          rule: '<12个月',
          value: methods.formatMoney(summary?.paybackPeriod, '个月'),
          qualified: Number.isFinite(paybackPeriod) && paybackPeriod > 0 && paybackPeriod < 12,
        },
        {
          label: '净利率',
          rule: '>15%',
          value: methods.formatPercent(summary?.netProfitRate),
          qualified: Number.isFinite(netProfitRate) && netProfitRate > 15,
        },
      ];
    },
    getEvaluationResult() {
      const items = methods.getEvaluationItems();
      return {
        items,
        passed: items.every(item => item.qualified),
      };
    },
    renderMonthlySales(value: SmartDecisionValue) {
      return methods.formatWan(value);
    },
    renderMonNetProfit(value: SmartDecisionValue) {
      return methods.formatWan(value);
    },
    renderNetProfitRate(value: SmartDecisionValue) {
      return methods.formatPercent(value);
    },
    renderPriceToRentRatio(value: SmartDecisionValue) {
      return methods.formatPercent(value);
    },
    renderStoreCost(value: SmartDecisionValue) {
      return methods.formatWan(value);
    },
    renderPaybackPeriod(value: SmartDecisionValue) {
      return methods.formatMoney(value, '个月');
    },
    renderDailyPreservationAmount(value: SmartDecisionValue) {
      return methods.formatMoney(value, '元/日');
    },
    renderCPM(value: SmartDecisionValue) {
      // return isNotEmpty(value) ? `${value}元/千人次` : '-';
      if (isNotEmpty(value)) {
        return <>
          {value}
          <IconFont
            iconHref='iconquestion-o'
            className='ml-2'
            tooltipConfig={{
              title: `CPM = 月度租金及费用 ÷ 日均过店客流 × 1000，即每千人次过店客流的获取成本(${value}元/千人次)，越低说明点位性价比越高`
            }}
          />
        </>;
      }
      return '-';
    },
    getDecisionColumns() {
      return [
        { key: 'monthlySales', dataIndex: 'monthlySales', title: '月销售额', width: 100, render: methods.renderMonthlySales },
        { key: 'cpm', dataIndex: 'cpm', title: 'CPM', width: 100, render: methods.renderCPM },
        { key: 'monNetProfit', dataIndex: 'monNetProfit', title: '月净利润', width: 100, render: methods.renderMonNetProfit },
        { key: 'netProfitRate', dataIndex: 'netProfitRate', title: '净利率', width: 100, render: methods.renderNetProfitRate },
        { key: 'priceToRentRatio', dataIndex: 'priceToRentRatio', title: '租售比', width: 100, render: methods.renderPriceToRentRatio },
        { key: 'storeCost', dataIndex: 'storeCost', title: '开店成本', width: 100, render: methods.renderStoreCost },
        { key: 'paybackPeriod', dataIndex: 'paybackPeriod', title: '回本周期', width: 100, render: methods.renderPaybackPeriod },
        { key: 'dailyPreservationAmount', dataIndex: 'dailyPreservationAmount', width: 140, title: '日保本额', render: methods.renderDailyPreservationAmount },
        { key: 'brandSynergyCoefficient', dataIndex: 'brandSynergyCoefficient', width: 100, title: '协同系数' },

      ];
    },
    loadDecisionTable() {
      return Promise.resolve({
        dataSource: summary ? [{ ...summary, id: 'decision-summary' }] : [],
        count: summary ? 1 : 0,
      });
    },
    getFinancialSummary(data?: SmartDecisionSummary) {
      return [
        { label: 'CPM', value: methods.formatMoney(data?.cpm, '元/千人次') },
        { label: '月预估销售额', value: methods.formatWan(data?.monthlySales) },
        { label: '预估回本周期', value: methods.formatMoney(data?.paybackPeriod, '个月') },
        { label: '租售比', value: methods.formatPercent(data?.priceToRentRatio) },
        { label: '日保本额', value: methods.formatMoney(data?.dailyPreservationAmount, '元/日') },
      ];
    },
    renderFinancialSummary(item: { label: string; value: string }) {
      return (
        <div
          className={styles.financialSummaryCard}
          key={item.label}
        >
          <strong className='bold'>{item.value}</strong>
          <span>{item.label}</span>
        </div>
      );
    },
  });
  const evaluationResult = methods.getEvaluationResult();

  return (
    <div className={styles.detailCard}>
      <div className={styles.detailCardHeader}>
        <div className={styles.cardTit}>
          <div className='fs-16 c-222 fw-600'>智能测算与决策</div>
          <div className={styles.AICon}>AI测算</div>
        </div>
        <div className='fs-12 c-999'>相似门店数据 · 客流转化模型 · 品牌协同系数</div>
      </div>
      <V2Table
        key={JSON.stringify(summary || {})}
        rowKey='id'
        defaultColumns={methods.getDecisionColumns()}
        onFetch={methods.loadDecisionTable}
        pagination={false}
        emptyRender={<div className='c-999'>暂无内容</div>}
        hideColumnPlaceholder
        className={styles.decisionTable}
      />
      <p className={styles.decisionDesc}>
        该点位
        {isNotEmpty(data?.mallLevel) && <span>位于<span className='c-006'>{methods.formatMallLevel(data?.mallLevel)}</span>商场核心位置</span>}
        {methods.getSynergyDescription(data?.brandSynergyModule?.weightedCoefficient) && <span>，<span className='c-006'>{methods.getSynergyDescription(data?.brandSynergyModule?.weightedCoefficient)}</span>（<span className='c-006'>{methods.formatCoefficient(data?.brandSynergyModule?.weightedCoefficient)}</span>）</span>}
        。
        {methods.hasSimilarStoreBaseline() && <span>以同{isNotEmpty(data?.mallLevel) && <span className='c-006'>{methods.formatMallLevel(data?.mallLevel)}</span>}商场{data?.similarStoreModule?.stores?.length ? <span className='c-006'>{data.similarStoreModule.stores.length}</span> : ''}家相似门店日均销售额均值<span className='c-006'>{methods.formatMoney(data?.similarStoreModule?.average?.dailySales, '元')}</span>为测算基准，</span>}
        {methods.getFlowAdvantage() && <span>叠加本点位客流优势（<span className='c-006'>{methods.formatMoney(data?.salesEstimation?.dailyFlow, '人/日')}</span>，<span className='c-006'>{methods.getFlowAdvantage()}</span>）</span>}
        {isNotEmpty(data?.salesEstimation?.dailySales) && <span>经品牌协同系数修正，日均销售额<span className='c-006'>{methods.formatMoney(data?.salesEstimation?.dailySales, '元')}</span></span>}
        {isNotEmpty(summary?.monthlySales) && <span>，月销售额<span className='c-006'>{methods.formatWan(summary?.monthlySales)}元</span></span>}
        {(isNotEmpty(data?.commercialConditionDetail?.businessType) || isNotEmpty(methods.getMonthlyRentAndExpense())) && <span>；{methods.getBusinessDescription()}{isNotEmpty(methods.getMonthlyRentAndExpense()) && <>；月度租金及费用合计<span className='c-006'>{methods.formatMoney(methods.getMonthlyRentAndExpense(), '元/月')}</span></>}</span>}
        {isNotEmpty(summary?.priceToRentRatio) && <span>，租售比<span className='c-006'>{methods.formatPercent(summary?.priceToRentRatio)}</span></span>}
        {isNotEmpty(summary?.netProfitRate) && <span>，净利率<span className='c-006'>{methods.formatPercent(summary?.netProfitRate)}</span></span>}
        {isNotEmpty(methods.getOneTimeInvestment()) && <span>；一次性投入<span className='c-006'>{methods.formatWan(methods.getOneTimeInvestment())}元</span></span>}
        {isNotEmpty(summary?.paybackPeriod) && <span>，回本周期约<span className='c-006'>{methods.formatMoney(summary?.paybackPeriod, '个月')}</span></span>}
        。
      </p>
      <div className={styles.finalConclusion}>
        <span>综合评估：</span>
        <strong className={evaluationResult.passed ? styles.evaluationPassed : styles.evaluationFailed}>
          {evaluationResult.passed ? '建议推进签约' : '不建议签约'}
        </strong>
        <span>。判定规则：</span>
        {evaluationResult.items.map((item, index) => (
          <span key={item.label}>
            {index > 0 ? '、' : ''}
            {item.label} {item.rule}（实际 {item.value}{' '}
            <strong className={item.qualified ? styles.evaluationPassed : styles.evaluationFailed}>
              {item.qualified ? '✓' : '×'}
            </strong>）
          </span>
        ))}
        <span>，{evaluationResult.passed ? '三项全部达标。' : '存在未达标项。'}</span>
      </div>
      <div className={styles.financialSummaryGrid}>
        {methods.getFinancialSummary(summary).map(methods.renderFinancialSummary)}
      </div>
    </div>
  );
};

export default Conclusion;
